import type { CookingLevel } from "../settings/types";

/** What a recipe *is*. Whether it suits you is what the settings answer. */
export type RecipeDiet = "omnivore" | "vegetarian" | "vegan";

/**
 * One instruction, at two depths.
 *
 * `text` is the instruction itself, written the way you'd say it to someone who
 * has cooked before. `detail` is the part a confident cook already knows and a
 * beginner is stuck without — how you tell it's done, what it should look like,
 * what goes wrong. Splitting them is what lets one recipe serve both without
 * being condescending to one or useless to the other.
 */
export interface RecipeStep {
  text: string;
  detail?: string;
}

export interface Recipe {
  id: string;
  title: string;
  /**
   * Ingredient names as a person would write them on a list. Matched loosely
   * against inventory names, so "chicken" finds "Chicken thighs".
   */
  ingredients: string[];
  steps: RecipeStep[];
  /**
   * Technique, variations, and the reasons behind the steps. Shown only at the
   * advanced level, where the instructions themselves are not the useful part.
   */
  notes?: string[];
  /** Roughly, at the pace of someone not rushing. */
  minutes: number;
  serves: number;

  /**
   * Per serving. Estimates from typical quantities, not laboratory figures —
   * they exist so bulk/cut/maintain can rank sensibly and so you can see at a
   * glance whether a meal is worth cooking. This is a label, not tracking.
   */
  protein: number;
  calories: number;

  /**
   * Replaced a hand-written `dietaryModes` array that mixed goals with
   * restrictions — the same recipe listed "bulk" and "vegan" side by side as if
   * they were the same kind of thing. Being vegan is a fact about the recipe;
   * bulking is a fact about the cook.
   */
  diet: RecipeDiet;

  tags: string[];
  difficulty: CookingLevel;
}

/** Protein per 100 calories — the number a cut actually cares about. */
export function proteinDensity(recipe: Recipe): number {
  if (recipe.calories <= 0) return 0;
  return (recipe.protein / recipe.calories) * 100;
}
